import React, { useState, useEffect } from "react";
import { Row, Col, Button } from "antd";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import ProductSkeleton from "./ProductSkeleton";
import styled from "styled-components";
import "aos/dist/aos.css";

const SectionWrapper = styled.div`
  padding: 60px 0;
  background: ${(props) => props.background || "#fff"};

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .section-header {
    text-align: center;
    margin-bottom: 50px;

    h2 {
      font-size: 2.5rem;
      font-weight: bold;
      margin-bottom: 15px;
      color: #333;

      @media (max-width: 768px) {
        font-size: 2rem;
      }
    }

    p {
      color: #666;
      font-size: 16px;
      max-width: 600px;
      margin: 0 auto;
    }
  }

  .view-all-btn {
    display: block;
    margin: 40px auto 0;
    background: #000 !important;
    color: white !important;
    border: none !important;
    padding: 10px 30px !important;
    height: auto !important;
    border-radius: 4px !important;
    transition: all 0.3s ease !important;

    &:hover {
      background: #fd8e07 !important;
      color: #000 !important;
    }

    &:active,
    &:focus {
      background: #fd8e07 !important;
      color: #000 !important;
    }
  }
`;

const ProductSection = ({
  title,
  subtitle,
  products: initialProducts = [],
  background,
  showViewAll = true,
  apiEndpoint, // Optional: if provided, fetch from API instead of using props
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(initialProducts);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (apiEndpoint) {
      // Fetch products from API
      const fetchProducts = async () => {
        try {
          setLoading(true);
          const response = await fetch(apiEndpoint);

          if (!response.ok) {
            throw new Error("Failed to fetch products");
          }

          const data = await response.json();

          if (data.status && data.data) {
            // Map API response to component format and calculate ratings
            const mappedProducts = data.data.map((product) => {
              const reviews = product.reviews || [];
              const averageRating =
                reviews.length > 0
                  ? Math.round(
                      reviews.reduce((sum, review) => sum + review.rating, 0) /
                        reviews.length,
                    )
                  : 0;

              const primaryImage = product.images?.[0];

              return {
                id: product.id,
                title: product.name,
                price: parseFloat(product.base_price),
                image: primaryImage?.url,
                rating: averageRating,
                reviews: reviews,
                brand: product.brand,
                category: product.category?.name,
                slug: product.slug,
                description: product.short_description || product.description,
                images: product.images,
                original_product: product, // Store full product data
              };
            });

            setProducts(mappedProducts);
          }

          setError(null);
          setLoading(false);
        } catch (err) {
          console.error("Error fetching products:", err);
          setError(err.message);
          setLoading(false);
        }
      };

      fetchProducts();
    } else {
      // Use provided products
      setProducts(initialProducts);
      setLoading(false);
    }
  }, []);

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug}`, { state: { product } });
  };

  const handleAddToCart = (product) => {};

  const handleAddToWishlist = (product) => {};

  const handleQuickView = (product) => {};

  return (
    <SectionWrapper background={background}>
      <div className="container">
        <div className="section-header">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>

        <Row gutter={[24, 24]}>
          {loading
            ? // Show skeletons while loading
              Array.from({ length: products.length || 8 }).map((_, index) => (
                <Col key={index} xs={24} sm={12} md={8} lg={6}>
                  <ProductSkeleton />
                </Col>
              ))
            : // Show actual products after loading
              products.map((product, index) => (
                <Col
                  key={index}
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                  // data-aos="fade-up"
                  // data-aos-delay={index * 100}
                >
                  <ProductCard
                    {...product}
                    onClick={() => handleProductClick(product)}
                    onAddToCart={() => handleAddToCart(product)}
                    onAddToWishlist={() => handleAddToWishlist(product)}
                    onQuickView={() => handleQuickView(product)}
                  />
                </Col>
              ))}
        </Row>

        {showViewAll && (
          <Button
            className="view-all-btn"
            size="large"
            onClick={() => {
              navigate("/shop");
            }}
          >
            View All Products
          </Button>
        )}
      </div>
    </SectionWrapper>
  );
};

export default ProductSection;
